import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { JourneyStage, UserProgress, StageProgress, User } from '@/types'
import { generateId } from '@/lib/utils'
import { saveUser, updateUserProgress } from '@/lib/supabase'
import { analyticsEvents, getUserSegment, trackUserJourneyProgress, AnalyticsTimer } from '@/lib/analytics'

// Navigation callback - will be set by app initialization
let navigateCallback: ((path: string) => void) | null = null

export const setNavigateCallback = (navigate: (path: string) => void) => {
  navigateCallback = navigate
}

interface JourneyState {
  // User data
  user: User | null
  
  // Journey configuration
  stages: JourneyStage[]
  currentStage: number
  userProgress: UserProgress | null
  
  // Session data
  sessionId: string
  
  // Actions
  initializeUser: (userData: Partial<User>) => Promise<void>
  setCurrentStage: (stageId: number) => void
  completeStage: (stageId: number, data?: Record<string, any>) => void
  updateStageProgress: (stageId: number, progressData: Partial<StageProgress>) => void
  resetJourney: () => void
  updateUser: (userData: Partial<User>) => void
}

const initialStages: JourneyStage[] = [
  {
    id: 0,
    title: "AI Landscape Overview",
    subtitle: "Understanding the AI ecosystem",
    description: "Comprehensive introduction to AI tools, automation types, and intelligent agents in today's business landscape.",
    icon: "Presentation",
    estimatedTime: 15,
    isCompleted: false,
    isActive: true,
    isLocked: false
  },
  {
    id: 1,
    title: "First AI Conversation",
    subtitle: "Break the ice with AI",
    description: "Learn the fundamentals of AI interaction through guided prompting exercises and real-time feedback.",
    icon: "MessageCircle",
    estimatedTime: 10,
    isCompleted: false,
    isActive: false,
    isLocked: true
  },
  {
    id: 2,
    title: "AI Creative Partnership",
    subtitle: "Collaborate with AI",
    description: "Experience AI as a creative partner in content creation, brainstorming, and collaborative work.",
    icon: "Users",
    estimatedTime: 12,
    isCompleted: false,
    isActive: false,
    isLocked: true
  },
  {
    id: 3,
    title: "Process Automation",
    subtitle: "Build your first workflow",
    description: "Design and simulate automated workflows using drag-and-drop tools and visual builders.",
    icon: "Workflow",
    estimatedTime: 15,
    isCompleted: false,
    isActive: false,
    isLocked: true
  },
  {
    id: 4,
    title: "Business Intelligence",
    subtitle: "Analyze and gain insights",
    description: "Upload data and experience AI-powered analysis, visualization, and business intelligence.",
    icon: "BarChart3",
    estimatedTime: 12,
    isCompleted: false,
    isActive: false,
    isLocked: true
  },
  {
    id: 5,
    title: "Personalized Roadmap",
    subtitle: "Your AI implementation plan",
    description: "Generate a customized AI adoption strategy based on your journey data and business needs.",
    icon: "Map",
    estimatedTime: 8,
    isCompleted: false,
    isActive: false,
    isLocked: true
  }
]

export const useJourneyStore = create<JourneyState>()(
  persist(
    (set, get) => ({
      user: null,
      stages: initialStages,
      currentStage: 0,
      userProgress: null,
      sessionId: generateId(),

      initializeUser: async (userData) => {
        const user: User = {
          id: generateId(),
          name: userData.name || 'Anonymous',
          email: userData.email,
          emailConsent: userData.emailConsent,
          businessType: userData.businessType,
          industry: userData.industry,
          teamSize: userData.teamSize,
          currentAIExperience: userData.currentAIExperience || 'none',
          primaryGoals: userData.primaryGoals,
          createdAt: new Date()
        }

        const userProgress: UserProgress = {
          userId: user.id,
          currentStage: 0,
          stageProgress: {}
        }

        // Save to database if email consent is provided or if user provided any data worth saving
        try {
          if (userData.email || userData.name || userData.businessType) {
            await saveUser({
              name: user.name,
              email: user.email,
              emailConsent: user.emailConsent,
              businessType: user.businessType,
              industry: user.industry,
              teamSize: user.teamSize,
              currentAIExperience: user.currentAIExperience,
              primaryGoals: user.primaryGoals
            })
            console.log('User data saved to database successfully')
          }
        } catch (error) {
          console.warn('Failed to save user data to database:', error)
          // Continue with local storage even if database save fails
        }

        // Track analytics for user onboarding completion
        analyticsEvents.onboardingCompleted({
          businessType: user.businessType,
          industry: user.industry,
          aiExperience: user.currentAIExperience,
          primaryGoals: user.primaryGoals,
          emailConsent: user.emailConsent
        })

        // Track email consent separately
        if (user.emailConsent && user.email) {
          analyticsEvents.emailConsentGiven(getUserSegment(user))
        }

        // Track user segment and goals
        if (user.primaryGoals) {
          user.primaryGoals.forEach(goal => {
            analyticsEvents.goalSelected(goal, getUserSegment(user))
          })
        }

        if (user.industry && user.businessType) {
          analyticsEvents.industrySelected(user.industry, user.businessType)
        }

        set({ user, userProgress })
      },

      setCurrentStage: (stageId) => {
        const { stages, userProgress, user } = get()
        
        // Track stage started event
        const stage = stages.find(s => s.id === stageId)
        if (stage) {
          analyticsEvents.stageStarted(stageId, stage.title)
        }
        
        // Update stages lock/active state
        const updatedStages = stages.map(stage => ({
          ...stage,
          isActive: stage.id === stageId,
          isLocked: stage.id > stageId && !stage.isCompleted
        }))

        // Update user progress
        const updatedProgress = userProgress ? {
          ...userProgress,
          currentStage: stageId
        } : null

        // Track journey progress
        if (user && userProgress) {
          const createdAtTime = user.createdAt instanceof Date 
            ? user.createdAt.getTime() 
            : new Date(user.createdAt).getTime()
          
          trackUserJourneyProgress(
            stageId,
            stages.length,
            getUserSegment(user),
            Date.now() - createdAtTime
          )
        }

        set({ 
          currentStage: stageId, 
          stages: updatedStages,
          userProgress: updatedProgress
        })
      },

      completeStage: (stageId, data) => {
        const { stages, userProgress, user } = get()
        
        // Track stage completion
        const stage = stages.find(s => s.id === stageId)
        if (stage) {
          analyticsEvents.stageCompleted(stageId, stage.title)
        }
        
        // Mark stage as completed
        const updatedStages = stages.map(stage => {
          if (stage.id === stageId) {
            return { ...stage, isCompleted: true, completionData: data }
          }
          // Unlock next stage
          if (stage.id === stageId + 1) {
            return { ...stage, isLocked: false }
          }
          return stage
        })

        // Update stage progress
        const stageProgress: StageProgress = {
          stageId,
          isCompleted: true,
          interactionData: data || {},
          skillsLearned: [], // Will be populated based on stage
          confidence: 7, // Default confidence after completion
          completedAt: new Date()
        }

        const updatedProgress = userProgress ? {
          ...userProgress,
          stageProgress: {
            ...userProgress.stageProgress,
            [stageId]: stageProgress
          }
        } : null

        // Track recommendations if any were generated
        if (data && data.recommendations) {
          data.recommendations.forEach((rec: any) => {
            analyticsEvents.recommendationGenerated(stageId, rec.type, rec.matchScore)
          })
        }

        set({ 
          stages: updatedStages, 
          userProgress: updatedProgress 
        })

        // Auto-advance to next stage if not the last one
        if (stageId < stages.length - 1) {
          setTimeout(() => {
            const nextStageId = stageId + 1
            
            // Update current stage and make sure next stage remains unlocked
            const currentState = get()
            const updatedStagesForNext = currentState.stages.map(stage => ({
              ...stage,
              isActive: stage.id === nextStageId,
              // Preserve unlock status from completeStage - don't re-lock next stage
              isLocked: stage.id > nextStageId && !stage.isCompleted
            }))

            const updatedProgressForNext = currentState.userProgress ? {
              ...currentState.userProgress,
              currentStage: nextStageId
            } : null

            set({ 
              currentStage: nextStageId, 
              stages: updatedStagesForNext,
              userProgress: updatedProgressForNext
            })
            
            // Navigate after state is fully updated
            if (navigateCallback) {
              navigateCallback(`/stage/${nextStageId}`)
            }
          }, 1000)
        } else {
          // For the final stage, track journey completion and navigate to dashboard
          if (user && userProgress) {
            const createdAtTime = user.createdAt instanceof Date 
              ? user.createdAt.getTime() 
              : new Date(user.createdAt).getTime()
            
            const totalTime = Math.round((Date.now() - createdAtTime) / (1000 * 60))
            const completedStages = Object.keys(updatedProgress?.stageProgress || {}).length
            analyticsEvents.journeyCompleted(totalTime, completedStages, !!user.email)
          }
          
          setTimeout(() => {
            if (navigateCallback) {
              navigateCallback('/')
            }
          }, 2000)
        }
      },

      updateStageProgress: (stageId, progressData) => {
        const { userProgress } = get()
        if (!userProgress) return

        const currentProgress = userProgress.stageProgress[stageId] || {
          stageId,
          isCompleted: false,
          interactionData: {},
          skillsLearned: [],
          confidence: 5
        }

        const updatedProgress = {
          ...userProgress,
          stageProgress: {
            ...userProgress.stageProgress,
            [stageId]: { ...currentProgress, ...progressData }
          }
        }

        set({ userProgress: updatedProgress })
      },

      resetJourney: () => {
        set({
          user: null,
          stages: initialStages,
          currentStage: 0,
          userProgress: null,
          sessionId: generateId()
        })
      },

      updateUser: (userData) => {
        const { user } = get()
        if (user) {
          set({ user: { ...user, ...userData } })
        }
      }
    }),
    {
      name: 'ai-journey-store',
      version: 1
    }
  )
)